import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type InfiniteData } from '@tanstack/react-query'
import { DropzoneRef } from 'react-dropzone'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
} from 'react-icons/hi2'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/common/Dialog'
import { ShinyProgressBar } from '@/components/common/ProgressBar/ShinyProgressBar'
import Switch from '@/components/common/Switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import FileUpload, { CompactLocalFile } from '@/components/files/FileUpload'
import { MAX_FILE_SIZE } from '@/components/files/FileUpload'
import {
  type SameNameMatch,
  SameNameFilesDialog,
  type StoredFileRef,
  splitBySameName,
} from '@/components/files/SameNameFilesDialog'
import VisibilityToggle from '@/components/permissions/PermissionsForm/VisibilityToggle'
import { Tag } from '@/components/tags/Tag'

import { describeFiles, getFileSizeAsHumanReadable } from '@/helpers/file'

import { useUploadDialogSync } from '@/hooks/common/useUploadDialogSync'
import { useUploadFileVersion } from '@/hooks/files/useFileVersions'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'
import { useUploadOrganizationFilesPresigned } from '@/hooks/organization/useUploadOrganizationFilesPresigned'
import { useTags } from '@/hooks/tags/useTags'

import type { PaginatedResponse } from '@/types/Api'
import type { File as UploadedFile, UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { Tag as TagType } from '@/types/Tag'

export interface UploadFileFormData {
  files: File[]
  tag_ids: UUID[]
  folder_id?: UUID
  extract_entities: boolean
  unrestricted: boolean
}

interface UploadFileDialogProps {
  open: boolean
  initialValues?: Partial<UploadFileFormData>
  setOpen: (open: boolean) => void
  autoOpen?: boolean
  showAutoExtractEntities?: boolean
  onSuccess?: (files: UploadedFile[]) => void
}

export const UPLOAD_FILE_DESCRIPTION =
  'Files give Sweetspot AI a better understanding of your organization. This includes files such as winning proposals, capabilities statements, resumes, technical documentation, and more.'

export const UPLOAD_FILE_ERROR_MESSAGE = `Failed to upload some files. If the problem persists, please verify that your files are not corrupted, not password protected, and are each below the ${getFileSizeAsHumanReadable(MAX_FILE_SIZE)} size limit. If the problem continues, please contact support.`

/** How an upload came out. */
interface UploadOutcome {
  created: UploadedFile[]
  /** The files that failed before a record was made, or all of them when the upload itself failed. */
  toRetry: File[]
  /** Whether anything failed, with or without something to try again. */
  failed: boolean
}

const NOTHING_UPLOADED: UploadOutcome = {
  created: [],
  toRetry: [],
  failed: false,
}

const uploadedMessage = (created: number, extractEntities: boolean): string =>
  `File${created === 1 ? '' : 's'} uploaded successfully!${extractEntities ? ' Scanning will begin automatically.' : ''}`

/** A submission held back until the user says what to do about the namesakes. */
interface SameNameSubmission {
  data: UploadFileFormData
  matches: SameNameMatch<File>[]
  fresh: File[]
}

const selectStoredFiles = (
  data: PaginatedResponse<UnhydratedFile>,
): StoredFileRef[] =>
  data.data.flatMap((file) =>
    file.organization_file
      ? [
          {
            id: file.id,
            name: file.name,
            folder_id: file.organization_file.folder_id ?? null,
            current_version_id: file.current_version_id ?? null,
          },
        ]
      : [],
  )

export const UploadFileDialog: React.FC<UploadFileDialogProps> = ({
  open,
  initialValues,
  setOpen,
  autoOpen = false,
  showAutoExtractEntities = false,
  onSuccess,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { mutateAsync: uploadFiles, isPending: isUploadingFiles } =
    useUploadOrganizationFilesPresigned({
      orgId: activeOrgId,
      onProgress: setUploadProgress,
      rejectOnPartialFailure: false,
    })
  const { mutateAsync: uploadVersion } = useUploadFileVersion({
    orgId: activeOrgId,
  })
  const [isReplacing, setIsReplacing] = useState(false)
  const [sameName, setSameName] = useState<SameNameSubmission | null>(null)

  const { data: storedFiles } = useOrganizationFiles({
    orgId: activeOrgId,
    enabled: open,
    selector: selectStoredFiles,
  })

  const fileUploadRef = useRef<DropzoneRef>(null)

  const methods = useForm<UploadFileFormData>({
    defaultValues: {
      files: initialValues?.files ?? [],
      tag_ids: initialValues?.tag_ids ?? [],
      extract_entities: initialValues?.extract_entities ?? false,
      folder_id: initialValues?.folder_id,
      unrestricted: true,
    },
  })

  const { control, handleSubmit, reset, setValue, watch } = methods

  const selectedFiles = watch('files')
  const selectedTagIds = watch('tag_ids')
  const initialFormValues = useMemo(
    () => ({
      files: initialValues?.files ?? [],
      tag_ids: initialValues?.tag_ids ?? [],
      folder_id: initialValues?.folder_id,
      extract_entities: initialValues?.extract_entities ?? false,
      unrestricted: initialValues?.unrestricted ?? true,
    }),
    [
      initialValues?.extract_entities,
      initialValues?.files,
      initialValues?.folder_id,
      initialValues?.tag_ids,
      initialValues?.unrestricted,
    ],
  )

  const selectTags = useCallback(
    (data: InfiniteData<PaginatedResponse<TagType>>) => {
      return data.pages
        .map((page) => page.data)
        .flat()
        .filter((tag) => selectedTagIds.includes(tag.id))
    },
    [selectedTagIds],
  )

  const { data: tags } = useTags({ orgId: activeOrgId, selector: selectTags })

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const uploadFresh = useCallback(
    async (data: UploadFileFormData): Promise<UploadOutcome> => {
      try {
        setErrorMessage(null)

        const res = await uploadFiles({
          files: data.files,
          tag_ids: data.tag_ids,
          folder_id: data.folder_id,
          extract_entities: data.extract_entities,
          unrestricted: data.unrestricted,
        })
        setUploadProgress(0)
        if (res.created.length > 0) {
          onSuccess?.(res.created)
        }
        return {
          created: res.created,
          toRetry: res.failed
            .filter((failedUpload) => !failedUpload.fileCreated)
            .map((failedUpload) => failedUpload.file),
          failed: res.failed.length > 0,
        }
      } catch (error) {
        console.error(error)
        return { created: [], toRetry: data.files, failed: true }
      }
    },
    [uploadFiles, setUploadProgress, onSuccess],
  )

  /**
   * Closes and says what landed, or says what failed and leaves it in the form
   * for another try. A null message is a replacement that wrote no version and
   * has already said so.
   */
  const settle = useCallback(
    ({ failed, toRetry }: UploadOutcome, done: string | null) => {
      if (failed) {
        setErrorMessage(UPLOAD_FILE_ERROR_MESSAGE)
        setValue('files', toRetry)
      } else {
        handleClose()
        if (done) {
          toast.success(done)
        }
      }
    },
    [handleClose, setValue],
  )

  const onSubmit = useCallback(
    async (data: UploadFileFormData) => {
      const { matches, fresh } = splitBySameName(
        data.files,
        storedFiles ?? [],
        data.folder_id ?? null,
      )
      if (matches.length === 0) {
        const uploaded = await uploadFresh(data)
        settle(
          uploaded,
          uploadedMessage(uploaded.created.length, data.extract_entities),
        )
        return
      }
      setSameName({ data, matches, fresh })
    },
    [storedFiles, uploadFresh, settle],
  )

  const handleSameNameCancel = useCallback(() => setSameName(null), [])

  const handleKeepBoth = useCallback(async () => {
    if (!sameName) {
      return
    }
    const { data } = sameName
    setSameName(null)
    const uploaded = await uploadFresh(data)
    settle(
      uploaded,
      uploadedMessage(uploaded.created.length, data.extract_entities),
    )
  }, [sameName, uploadFresh, settle])

  // Each namesake becomes a new version of the file it matched while the rest
  // upload as usual. Whatever failed stays in the form for another try.
  const handleReplace = useCallback(async () => {
    if (!sameName) {
      return
    }
    const { data, matches, fresh } = sameName
    setSameName(null)
    setErrorMessage(null)
    setIsReplacing(true)
    try {
      const [outcomes, uploaded] = await Promise.all([
        Promise.allSettled(
          matches.map(({ file, stored }) =>
            uploadVersion({ fileId: stored.id, file }),
          ),
        ),
        fresh.length > 0
          ? uploadFresh({ ...data, files: fresh })
          : NOTHING_UPLOADED,
      ])
      const replaced: UploadedFile[] = []
      const unchanged: UploadedFile[] = []
      const notReplaced: File[] = []
      matches.forEach(({ file, stored }, index) => {
        const outcome = outcomes[index]
        if (outcome.status !== 'fulfilled') {
          notReplaced.push(file)
        } else if (
          outcome.value.current_version_id === stored.current_version_id
        ) {
          unchanged.push(outcome.value)
        } else {
          replaced.push(outcome.value)
        }
      })
      const landed = [...replaced, ...unchanged]
      if (landed.length > 0) {
        onSuccess?.(landed)
      }
      if (unchanged.length > 0) {
        toast.info(
          `Nothing changed in ${describeFiles(unchanged)}, so ${
            unchanged.length === 1
              ? 'no new version was'
              : 'no new versions were'
          } saved.`,
        )
      }
      settle(
        {
          created: uploaded.created,
          toRetry: [...notReplaced, ...uploaded.toRetry],
          failed: uploaded.failed || notReplaced.length > 0,
        },
        fresh.length > 0
          ? uploadedMessage(uploaded.created.length, data.extract_entities)
          : replaced.length > 0
            ? `New version${replaced.length === 1 ? '' : 's'} uploaded.`
            : null,
      )
    } finally {
      setIsReplacing(false)
    }
  }, [sameName, uploadVersion, uploadFresh, onSuccess, settle])

  const handleAddFiles = useCallback(
    (files: File[]) => {
      setValue('files', [...selectedFiles, ...files])
    },
    [setValue, selectedFiles],
  )

  const handleRemoveFile = useCallback(
    (idx: number) => () => {
      setValue(
        'files',
        selectedFiles.filter((_, i) => i !== idx),
      )
    },
    [setValue, selectedFiles],
  )

  const handleRemoveTag = useCallback(
    (tagId: UUID) => () => {
      setValue(
        'tag_ids',
        selectedTagIds.filter((id) => id !== tagId),
      )
    },
    [setValue, selectedTagIds],
  )

  const handleResetForm = useCallback(() => {
    setUploadProgress(0)
    setErrorMessage(null)
    reset(initialFormValues)
  }, [initialFormValues, reset])

  useUploadDialogSync({
    open,
    files: initialValues?.files,
    onOpen: handleResetForm,
    onDeliver: handleAddFiles,
  })

  useEffect(() => {
    if (open && autoOpen) {
      // give the dialog time to open before opening the file upload
      const timeoutId = setTimeout(() => {
        fileUploadRef.current?.open()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [autoOpen, open])

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          aria-describedby={undefined}
          className="sm:max-w-[720px] w-full pt-5 pb-4 px-5 space-y-4"
          closeButtonClassName="absolute right-3 -top-1"
        >
          <DialogTitle>Upload Files to Your Library</DialogTitle>
          <DialogDescription>{UPLOAD_FILE_DESCRIPTION}</DialogDescription>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {tags && tags.length > 0 && (
                <div className="flex flex-col gap-y-[6px]">
                  <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    Selected tags
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <Tag
                        key={tag.id}
                        tagId={tag.id}
                        onDelete={handleRemoveTag(tag.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
              <FileUpload ref={fileUploadRef} onAddFiles={handleAddFiles} />
              <Controller
                name="unrestricted"
                control={methods.control}
                defaultValue={true}
                render={({ field: { value, onChange } }) => (
                  <VisibilityToggle
                    isUnrestricted={value}
                    onChange={onChange}
                    resourceType="file"
                    parentResourceType="folder or organization"
                    isNew
                    isPlural={selectedFiles.length > 1}
                  />
                )}
              />
              {isUploadingFiles && (
                <div className="flex flex-col w-full">
                  <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                    Uploading file{selectedFiles.length === 1 ? '' : 's'} (
                    {Math.round(uploadProgress * 100)}%)...
                  </span>
                  <ShinyProgressBar
                    progress={uploadProgress}
                    className="mt-2"
                  />
                </div>
              )}
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <CompactLocalFile
                      key={index}
                      file={file}
                      onDelete={handleRemoveFile(index)}
                    />
                  ))}
                </div>
              )}
              {errorMessage && (
                <div className="flex items-start gap-2 mt-2">
                  <HiOutlineExclamationCircle
                    size={16}
                    className="flex-shrink-0 mt-0.5 text-red-600 dark:text-red-500"
                  />
                  <span className="text-sm text-red-600 dark:text-red-500">
                    {errorMessage}
                  </span>
                </div>
              )}
              <DialogFooter className="flex items-center justify-between pt-4 -mx-5 px-5 border-t border-primary-200 dark:border-primary-800">
                <div className="flex items-center gap-2">
                  {showAutoExtractEntities && (
                    <Controller
                      control={control}
                      name="extract_entities"
                      render={({ field }) => {
                        const toggle = () => {
                          field.onChange(!field.value)
                        }
                        return (
                          <div className="flex items-center gap-2">
                            <Switch
                              id="extract_entities"
                              checked={field.value}
                              onClick={toggle}
                            />
                            <label
                              htmlFor="extract_entities"
                              className="flex items-center gap-2"
                            >
                              <span className="select-none text-sm font-medium text-primary-900 dark:text-primary-100">
                                Auto-scan files?
                              </span>
                              <Tooltip>
                                <TooltipTrigger>
                                  <HiOutlineInformationCircle
                                    size={16}
                                    className="flex-shrink-0 text-primary-500 dark:text-primary-400"
                                  />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[300px] space-y-3 py-2">
                                  <p>
                                    Automatically scan files to identify people,
                                    companies, and other key data when uploading
                                    completes.
                                  </p>
                                  <p>
                                    Identified data will be searchable in your
                                    Library and available to Sweetspot AI across
                                    the platform.
                                  </p>
                                  <p>
                                    Scanning runs after file indexing completes
                                    and typically takes a few minutes per file.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </label>
                          </div>
                        )
                      }}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline-compact"
                    type="button"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary-compact"
                    disabled={
                      isUploadingFiles ||
                      isReplacing ||
                      selectedFiles.length === 0
                    }
                    isLoading={isUploadingFiles || isReplacing}
                  >
                    <span>
                      Upload File{selectedFiles.length === 1 ? '' : 's'}
                    </span>
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <SameNameFilesDialog
        open={sameName !== null}
        names={sameName?.matches.map(({ stored }) => stored.name) ?? []}
        isPending={isReplacing}
        onReplace={handleReplace}
        onKeepBoth={handleKeepBoth}
        onCancel={handleSameNameCancel}
      />
    </>
  )
}
